import { useMemo } from "react";
import { UpcomingMatch } from "../../../types/types";
import { useLocalization } from "../../../hooks/useLocalization";
import { PLAYOFF_MIN_SLOTS } from "../constants";

export const getWinnerClass = (s1: number | undefined, s2: number | undefined) => {
  if (s1 === 0 && s2 === 0) return false;
  if (s1 != null && s2 != null) {
    return s1 > s2;
  }
  return false;
};

const createPlaceholderMatch = (
  bracket: "UPPER" | "LOWER",
  round: number,
  position: number,
): UpcomingMatch => ({
  id: -(round * 100 + position + (bracket === "LOWER" ? 50 : 0)),
  time: "",
  matchType: "PLAYOFF",
  link: "",
  bracket,
  bracketRound: round,
  bracketPosition: position,
});

const fillRoundSlots = (
  matches: UpcomingMatch[],
  bracket: "UPPER" | "LOWER",
  round: number,
  minSlots: number,
): UpcomingMatch[] => {
  const sorted = [...matches].sort((a, b) => (a.bracketPosition ?? 0) - (b.bracketPosition ?? 0));
  const byPosition = new Map<number, UpcomingMatch>();
  let autoPos = 1;

  for (const match of sorted) {
    let pos = match.bracketPosition && match.bracketPosition > 0 ? match.bracketPosition : autoPos;
    while (byPosition.has(pos)) {
      pos += 1;
    }
    byPosition.set(pos, match);
    autoPos = pos + 1;
  }

  const maxFromData = byPosition.size > 0 ? Math.max(...byPosition.keys()) : 0;
  const slotCount = Math.max(minSlots, maxFromData);

  const filled: UpcomingMatch[] = [];
  for (let position = 1; position <= slotCount; position++) {
    filled.push(byPosition.get(position) ?? createPlaceholderMatch(bracket, round, position));
  }
  return filled;
};

export const usePlayoffBracket = (playoffMatches: UpcomingMatch[] | undefined) => {
  const { strings } = useLocalization();

  const getPlaceholder = (bracket: string, round: number, side: "top" | "bottom") => {
    const { PLAYOFFCHART } = strings;
    if (round === 4) {
      return side === "top"
        ? PLAYOFFCHART.FINAL_UPPERBRACKET_WINNER
        : PLAYOFFCHART.FINAL_LOWERBRACKET_WINNER;
    }

    if (bracket === "UPPER") {
      if (round === 1) return strings.COMMON.TBD;
      return `${PLAYOFFCHART.ROUND} ${String(round - 1)} voittaja`;
    }

    if (round === 1) return strings.COMMON.TBD;
    return side === "top"
      ? PLAYOFFCHART.WINNER_LOWERBRACKET + String(round - 1)
      : PLAYOFFCHART.LOSER_UPPERBRACKET + String(round - 1);
  };

  const rounds = useMemo(() => {
    if (!playoffMatches) {
      return null;
    }

    const getMatches = (bracket: "UPPER" | "LOWER", round: number) =>
      playoffMatches.filter((m) => m.bracket === bracket && m.bracketRound === round);

    return {
      upper: [1, 2].map((round) =>
        fillRoundSlots(
          getMatches("UPPER", round),
          "UPPER",
          round,
          PLAYOFF_MIN_SLOTS.UPPER[round - 1] ?? 1,
        ),
      ),
      lower: [1, 2, 3].map((round) =>
        fillRoundSlots(
          getMatches("LOWER", round),
          "LOWER",
          round,
          PLAYOFF_MIN_SLOTS.LOWER[round - 1] ?? 1,
        ),
      ),
      grandFinal: playoffMatches.find((m) => m.bracketRound === 4),
    };
  }, [playoffMatches]);

  return {
    strings,
    rounds,
    getPlaceholder,
    getWinnerClass,
  };
};
